import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { FileText, Plus, Scissors, Calendar as CalendarIcon } from "lucide-react";
import { User } from "../../../types/user";
import { useAuth } from "../../../context/AuthContext";
import { updateUserAvailabilitySchedule } from "../../../lib/api";
import { getAvailabilityReport } from "../../../lib/api/report";
import { AvailabilityReport } from "./AvailabilityReport";
import { ModalWrapper, ModalHeader, ModalFooter, FormSelect, Button, ModalSuspense } from "../../common";

// Lazy load CalendarReport
const CalendarReport = lazy(() => import("./CalendarReport").then(m => ({ default: m.CalendarReport })));
import { useAvailabilityState } from "./hooks/useAvailabilityState";
import { useAvailabilityNavigation } from "./hooks/useAvailabilityNavigation";
import { useScheduleNavigation } from "./hooks/useScheduleNavigation";
import { NavigationControls } from "./components/NavigationControls";
import { ScheduleGrid } from "./components/ScheduleGrid";
import { Availability, WeeklySchedule } from "../../../lib/api/types";
import { TimeSlot } from "../../../../src/types/availability";
import { SplitScheduleModal } from "./components/SplitScheduleModal";
import toast from "react-hot-toast";
import { useUsers } from "../../../context/UserContext";
import { useApp } from "../../../context/AppContext";
import { useTranslation } from "../../../context/TranslationContext";

interface AvailabilityModalProps {
	colleague: User;
	onClose: () => void;
}

export function AvailabilityModal({
	colleague,
	onClose,
}: AvailabilityModalProps) {
	const { t } = useTranslation();
	const { token } = useAuth();
	const { currentUser, colleagues } = useApp();
	const { emit } = useUsers();
	if (!token || !currentUser) return null;

	const [showReport, setShowReport] = useState(false);
	const [showCalendarReport, setShowCalendarReport] = useState(false);
	const [reportData, setReportData] = useState<any>(null);
	const [reportYear, setReportYear] = useState(
		new Date().getFullYear().toString()
	);
	const [showSplitModal, setShowSplitModal] = useState(false);
	const [hasChanges, setHasChanges] = useState(false);
	const [selectedColleague, setSelectedColleague] = useState<User>(colleague);
	const isAdmin = currentUser?.role === 'admin';
	const isReadOnly = !isAdmin && currentUser?.id !== colleague.id;

	// Refs for input elements to maintain focus
	const startDateRef = useRef<HTMLInputElement>(null);
	const endDateRef = useRef<HTMLInputElement>(null);
	const repeatPatternRef = useRef<HTMLSelectElement>(null);

	const {
		loading,
		setLoading,
		setError,
		startDate,
		setStartDate,
		endDate,
		setEndDate,
		repeatPattern,
		setRepeatPattern,
		schedule,
		setSchedule,
		alternateSchedule,
		setAlternateSchedule,
		handleTimeSlotToggle,
	} = useAvailabilityState(selectedColleague);

	const {
		currentEntryIndex,
		totalEntries,
		handleFirstEntry,
		handlePrevEntry,
		handleNextEntry,
		handleLastEntry,
	} = useAvailabilityNavigation({
		colleague: selectedColleague,
		setStartDate,
		setEndDate,
		setRepeatPattern,
		setSchedule,
		setAlternateSchedule,
	});

	const { handleAdd, handleSplit } = useScheduleNavigation({
		token,
		colleague: selectedColleague,
		currentEntryIndex,
		setCurrentEntryIndex: () => { },
		setStartDate,
		setEndDate,
		setSchedule,
		setAlternateSchedule,
		setError,
	});

	// Update selected colleague when colleague prop changes
	useEffect(() => {
		setSelectedColleague(colleague);
	}, [colleague]);

	const handleViewReport = async () => {
		if (!token) return;

		const toastId = toast.loading(t('common.loading'));
		try {
			setLoading(true);
			setError("");

			const data = await getAvailabilityReport(
				token,
				selectedColleague.site,
				selectedColleague.id,
				reportYear
			);

			setReportData(data);
			setShowReport(true);
			toast.success(t('availability.reportLoaded'), { id: toastId });
		} catch (err) {
			const errorMessage =
				err instanceof Error
					? err.message
					: t('availability.failedToGenerateReport');
			setError(errorMessage);
			toast.error(errorMessage, { id: toastId });
		} finally {
			setLoading(false);
		}
	};

	const handleViewCalendarReport = () => {
		setShowCalendarReport(true);
	};

	const handleSave = async () => {
		if (!token || currentEntryIndex === -1 || isReadOnly) return;

		const toastId = toast.loading(t('common.saving'));
		try {
			setLoading(true);
			setError("");

			const availability: Availability = {
				weeklySchedule: schedule,
				...(repeatPattern === "evenodd" && {
					oddWeeklySchedule: alternateSchedule,
				}),
				startDate,
				endDate,
				repeatPattern,
			};

			await updateUserAvailabilitySchedule(
				selectedColleague.id,
				currentEntryIndex,
				availability
			);

			// Emit settings update event
			emit("settingsUpdated", {
				userId: selectedColleague.id,
				settings: {
					...currentUser.settings,
					availability: [
						...currentUser.settings?.availability?.slice(0, currentEntryIndex) || [],
						availability,
						...currentUser.settings?.availability?.slice(currentEntryIndex + 1) || []
					]
				}
			});

			setHasChanges(true);
			toast.success(t('availability.availabilityUpdated'), { id: toastId });
		} catch (err) {
			const errorMessage =
				err instanceof Error
					? err.message
					: t('availability.failedToUpdateSchedule');
			setError(errorMessage);
			toast.error(errorMessage, { id: toastId });
		} finally {
			setLoading(false);
		}
	};

	const handleCloseModal = () => {
		if (hasChanges) {
			// Trigger a refresh of the calendar grid
			emit("availabilityChanged", {
				userId: selectedColleague.id
			});
		}
		onClose();
	};

	const onTimeSlotToggle = (
		day: keyof WeeklySchedule,
		slot: keyof TimeSlot,
		isAlternate: boolean
	) => {
		if (isReadOnly) return;

		handleTimeSlotToggle(
			token,
			selectedColleague.id,
			currentEntryIndex,
			day,
			slot,
			isAlternate
		);
	};

	// Filter colleagues for admin dropdown - exclude admin users
	const filteredColleagues = isAdmin
		? colleagues.filter(c => c.id !== currentUser.id && c.role !== "admin")
		: [];

	const handleColleagueChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const selectedId = e.target.value;
		const newColleague = filteredColleagues.find(c => c.id === selectedId);
		if (newColleague) {
			setSelectedColleague(newColleague);
		}
	};

	// Handle input changes with focus preservation
	const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setStartDate(e.target.value);
		// No need to manually set focus as the input already has it
	};

	const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setEndDate(e.target.value);
		// No need to manually set focus as the input already has it
	};

	const handleRepeatPatternChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setRepeatPattern(e.target.value as "all" | "evenodd");
		// No need to manually set focus as the select already has it
	};

	return (
		<>
		<ModalWrapper data-tsx-id="availability-modal" onClose={handleCloseModal} size="2xl" closeOnClickOutside={false}>
			<ModalHeader
				icon={<CalendarIcon />}
				title={isAdmin ? undefined : t('availability.setAvailability')}
				onClose={handleCloseModal}
			>
				{isAdmin && (
					<div className="flex items-center space-x-3">
						<select
							value={selectedColleague.id}
							onChange={handleColleagueChange}
							className="rounded-md border-zinc-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
						>
							{filteredColleagues.map(c => (
								<option key={c.id} value={c.id}>
									{c.firstName} {c.lastName}{c.id === currentUser.id ? ' (You)' : ''}
								</option>
							))}
						</select>
						<h2 className="text-xl font-semibold text-zinc-900">
							{t('availability.setAvailabilityFor', { firstName: selectedColleague.firstName, lastName: selectedColleague.lastName })}
						</h2>
					</div>
				)}
			</ModalHeader>

				<div className="p-6 space-y-6">
					<div className="grid grid-cols-12 gap-4 items-end">
						<div className="col-span-2">
							<NavigationControls
								currentEntryIndex={currentEntryIndex}
								totalEntries={totalEntries}
								onFirstEntry={handleFirstEntry}
								onPrevEntry={handlePrevEntry}
								onNextEntry={handleNextEntry}
								onLastEntry={handleLastEntry}
								isNewEntry={currentEntryIndex === -1}
							/>
						</div>

						<div className="col-span-10 grid grid-cols-9 gap-2">
							<div className="p-2 col-span-3 border rounded-md border-zinc-300">
								<label className="block text-sm font-medium text-zinc-700 mb-0">
									{t('common.startDate')}
								</label>
								<div className="flex-1 flex items-center">
									<button
										onClick={() => !isReadOnly && handleAdd(true)}
										className={`space-x-2 ${isReadOnly ? "text-zinc-300 cursor-not-allowed hidden" :
											currentEntryIndex === -1 ||
												currentEntryIndex === 0
												? "text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
												: "text-zinc-300 cursor-not-allowed hidden"
											}`}
										title={t('availability.addScheduleBefore')}
										disabled={isReadOnly}
									>
										<Plus className="w-5 h-5" />
									</button>
									<input
										ref={startDateRef}
										type="date"
										value={startDate}
										onChange={handleStartDateChange}
										className={`w-32 ${currentEntryIndex === -1
											? "opacity-50 cursor-not-allowed hidden"
											: ""
											}`}
										disabled={currentEntryIndex === -1 || isReadOnly}
										readOnly={isReadOnly}
									/>
								</div>
							</div>

							<div className="col-span-1 flex items-end justify-center">
								<button
									onClick={() => setShowSplitModal(true)}
									className={`p-2 text-zinc-600 hover:bg-purple-50 rounded-full transition-colors ${isReadOnly || currentEntryIndex === -1 || !endDate
										? "opacity-50 cursor-not-allowed hidden"
										: ""
										}`}
									title={t('availability.splitSchedule')}
									disabled={
										isReadOnly || currentEntryIndex === -1 || !endDate
									}
								>
									<Scissors className="w-5 h-5" />
								</button>
							</div>

							<div className="p-2 col-span-3 border rounded-md border-zinc-300">
								<label className="block text-sm font-medium text-zinc-700 mb-0">
									{t('common.endDate')}
								</label>
								<div className="flex-1 flex items-center">
									<input
										ref={endDateRef}
										type="date"
										value={endDate}
										onChange={handleEndDateChange}
										min={startDate}
										className={`w-32 ${currentEntryIndex === -1
											? "opacity-50 cursor-not-allowed hidden"
											: ""
											}`}
										disabled={currentEntryIndex === -1 || isReadOnly}
										readOnly={isReadOnly}
									/>
									<button
										onClick={() => !isReadOnly && handleAdd(false)}
										className={`space-x-2 ${isReadOnly ? "text-zinc-300 cursor-not-allowed hidden" :
											currentEntryIndex === -1 ||
												currentEntryIndex ===
												totalEntries - 1
												? "text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
												: "text-zinc-300 cursor-not-allowed hidden"
											}`}
										title={t('availability.addScheduleAfter')}
										disabled={isReadOnly}
									>
										<Plus className="w-5 h-5" />
									</button>
								</div>
							</div>

							<div className="p-2 col-span-2 border rounded-md border-zinc-300">
								<div className="flex justify-end">
									<div className="w-48">
										<label className="block text-sm font-medium text-zinc-700 mb-1">
											{t('availability.repeatPattern')}
										</label>
										<select
											ref={repeatPatternRef}
											value={repeatPattern}
											onChange={handleRepeatPatternChange}
											className={`w-32 rounded-md border-zinc-300 ${currentEntryIndex === -1
												? "opacity-50 cursor-not-allowed hidden"
												: ""
												}`}
											disabled={currentEntryIndex === -1 || isReadOnly}
										>
											<option value="all">
												{t('availability.everyWeek')}
											</option>
											<option value="evenodd">
												{t('availability.alternateWeeks')}
											</option>
										</select>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="space-y-8 h-[348px]">
						{repeatPattern === "evenodd" && (
							<div>
								<ScheduleGrid
									caption={t('availability.evenWeeks')}
									schedule={schedule}
									onTimeSlotToggle={onTimeSlotToggle}
									disabled={currentEntryIndex === -1 || isReadOnly || !isAdmin}
								/>
							</div>
						)}

						<div>
							<ScheduleGrid
								caption={
									repeatPattern === "all"
										? t('availability.weeklySchedule')
										: t('availability.oddWeeks')
								}
								schedule={
									repeatPattern === "evenodd"
										? alternateSchedule
										: schedule
								}
								isAlternate={repeatPattern === "evenodd"}
								onTimeSlotToggle={onTimeSlotToggle}
								disabled={currentEntryIndex === -1 || isReadOnly || !isAdmin}
							/>
						</div>
					</div>
				</div>

			<ModalFooter alignment="left" className="flex justify-between">
				<div className="flex items-center space-x-2">
					<FormSelect
						value={reportYear}
						onChange={(e) => setReportYear(e.target.value)}
						options={[...Array(5)].map((_, i) => {
							const year = new Date().getFullYear() + i;
							return { value: year.toString(), label: year.toString() };
						})}
					/>
					<Button
						onClick={handleViewReport}
						variant="secondary"
						icon={<FileText />}
						disabled={loading}
					>
						{t('availability.viewReport')}
					</Button>
					<Button
						onClick={handleViewCalendarReport}
						variant="secondary"
						icon={<FileText />}
						disabled={loading}
					>
						{t('availability.viewCalendarReport')}
					</Button>
				</div>

				<div className="flex space-x-3">
					<Button
						onClick={handleCloseModal}
						variant="secondary"
						disabled={loading}
					>
						{t('common.cancel')}
					</Button>
					{isAdmin && (
						<Button
							onClick={handleSave}
							variant="primary"
							loading={loading}
							disabled={currentEntryIndex === -1 || isReadOnly}
						>
							{t('common.save')}
						</Button>
					)}
				</div>
			</ModalFooter>
		</ModalWrapper>

			{showReport && reportData && (
				<AvailabilityReport
					data={reportData}
					colleague={selectedColleague}
					onClose={() => setShowReport(false)}
				/>
			)}

			{showCalendarReport && (
				<ModalSuspense>
					<CalendarReport
						colleague={selectedColleague}
						year={reportYear}
						onClose={() => setShowCalendarReport(false)}
					/>
				</ModalSuspense>
			)}

			{showSplitModal && currentEntryIndex !== -1 && (
				<SplitScheduleModal
					startDate={startDate}
					endDate={endDate}
					onSplit={handleSplit}
					onClose={() => setShowSplitModal(false)}
				/>
			)}
		</>
	);
}